import { kioskCommand } from 'api/Kiosk';
import { updateInvoices } from 'api/Invoice';
import { createExternalInvoices } from 'api/Billing';
import { createInstantPacket } from 'api/InstantPacket';

import { ia, iaRa, mString } from 'lib/helpers/utility';

import { showAlert } from 'components/shared/Alert/Alert';
import Typography from 'components/shared/Typography/Typography';

import { amountWithoutGratuity } from '../lib/checkoutAmounts';

import { resolveKioskPaidAmount } from './planCharge';
import { chargePayrocPaymentHandler, saveCardHandler } from './payrocMethods';
import {
  chargeElavonOnlineHandler,
  elavonPreAuthDeferredHandler,
  elavonSavedCardHandler
} from './elavonMethods';
import {
  buildPacketLink,
  createPatientFormsPacket,
  documentPacketForms,
  packetPaymentPayload
} from './patientFormsPacket';
import {
  appliedBalanceHandler,
  chargeCashHandler,
  chargeCheckHandler,
  chargeOutsideFinancingHandler,
  noPaymentSelectedHandler
} from './genericMethods';

const sendCommandToKiosk = async (
  navigate,
  {
    methodDetail,
    amount,
    gratuity,
    invoices,
    appliedBalance,
    surchargeAmount,
    patientId,
    practiceId,
    data,
    terminalId,
    saveCard,
    recurringPayment,
    planId,
    scheduleId,
    updatePlanId,
    transactionTags,
    appointmentId,
    includedDocuments,
    patientFormIds,
    createPatientFromCCInfo,
    ..._rest
  }
) => {
  if (!methodDetail) {
    showAlert({
      color: 'danger',
      title: 'No kiosk selected',
      message: 'Please select a kiosk to send the payment request to.'
    });
    return { code: 1 };
  }

  // A plan only charges what is due today — the upfront part, or the whole amount when an
  // auto-recurring plan starts immediately. With nothing due, the kiosk just stores the card.
  // Plans take no tip; otherwise the tip travels to the reader on its own and stays out of the
  // invoice, which only carries what the sale is owed.
  const tip = recurringPayment ? 0 : Math.max(0, Math.round(Number(gratuity) || 0));
  const chargeAmount = recurringPayment
    ? resolveKioskPaidAmount(recurringPayment, { amount })
    : amountWithoutGratuity({ amount, gratuity: tip });

  if (recurringPayment && chargeAmount <= 0) {
    const saveCardUrl = `${window.location.origin}/kiosk/save-card?patientId=${encodeURIComponent(
      patientId ?? ''
    )}&practiceId=${encodeURIComponent(practiceId ?? '')}`;

    const saveCardRes = await kioskCommand(navigate, {
      kioskId: methodDetail,
      command: 'url-change',
      url: saveCardUrl,
      page: 'SaveCard',
      patientId,
      data: {
        ...data,
        terminalId,
        practiceId,
        patientId,
        saveCard: true,
        amount: 0,
        recurringPayment
      }
    });

    if (saveCardRes?.code !== 0) {
      throw new Error(saveCardRes);
    }

    showAlert({
      icon: 'outbound-fax-success',
      color: 'success',
      variant: 'light',
      title: 'Card request sent',
      message: (
        <Typography variant="p" size="small" className="block">
          Ask the patient to tap their card on kiosk{' '}
          <span className="font-semibold">{saveCardRes?.kioskName}</span>. No payment is taken.
        </Typography>
      ),
      duration: 15000
    });

    return {
      code: 0,
      kioskName: saveCardRes?.kioskName,
      kioskId: methodDetail,
      awaitingKiosk: true,
      isNothingDue: true,
      patientId: patientId ?? null
    };
  }

  await updateInvoices(navigate, { invoices });
  const internalDetails = iaRa(invoices).map(({ id, balance }) => ({
    id,
    amount_paid: balance
  }));

  const selectedInternalInvoices = [
    {
      amount_cents: Math.round(chargeAmount) - (surchargeAmount || 0),
      total_amount_cents: Math.round(chargeAmount),
      due_date: new Date(),
      internal_invoice_ids: iaRa(invoices).map((i) => i.id),
      memo: '',
      patient: { id: patientId },
      tax_percentage: 0,
      surcharge_amount: surchargeAmount,
      aeob: false,
      is_detailed: false,
      type: 'kiosk',
      internal_details: internalDetails,
      // The transaction is created on the kiosk side, so the picked tags ride on the invoice
      // and get applied when that payment lands.
      transaction_tag_ids: transactionTags,
      // Carry plan linkage so the kiosk payment can mark the installment/schedule paid.
      ...(planId && { payment_plan_id: planId }),
      ...(scheduleId && { payment_plan_schedule_id: scheduleId })
    }
  ];

  const newExternalInvoice = await createExternalInvoices(navigate, {
    invoices: selectedInternalInvoices,
    skipConfirmation: true
  });

  if (!ia(newExternalInvoice?.invoices)) {
    showAlert({
      color: 'danger',
      title: "Couldn't send the request to the kiosk",
      message: 'Please try adding an invoice.'
    });
    return;
  }

  const extInvoice = newExternalInvoice?.invoices?.[0];

  if (!extInvoice?.link) {
    showAlert({
      color: 'danger',
      title: "Couldn't send the request to the kiosk",
      message: 'No eligible invoices were selected for payment. Please select a different invoice.'
    });
    return;
  }

  let kioskUrl = extInvoice.link;

  if (ia(includedDocuments)) {
    const packet = await createPatientFormsPacket(navigate, {
      patientId: extInvoice?.patient_id ?? patientId,
      appointmentId,
      includedDocuments,
      patientFormIds,
      payment: packetPaymentPayload({
        amount: chargeAmount,
        surchargeAmount,
        invoices
      })
    });

    if (!packet?.token) {
      showAlert({
        color: 'danger',
        title: "Couldn't attach the patient forms",
        message: 'Please try again, or send the request without forms.'
      });
      return { code: 1 };
    }

    kioskUrl = buildPacketLink(packet.token, {
      autoPayCard: true,
      invoiceToken: extInvoice?.token
    });
  }

  const kioskCommandRes = await kioskCommand(navigate, {
    kioskId: methodDetail,
    command: 'url-change',
    url: kioskUrl,
    patientId: extInvoice?.patient_id,
    externalInvoiceToken: extInvoice?.token,
    page: 'Payment',
    data: {
      ...data,
      externalInvoiceToken: extInvoice?.token,
      gratuity: tip,
      terminalId,
      ...(createPatientFromCCInfo && !patientId && { createPatientFromCCInfo: true }),
      // A plan needs the card on file for its future charges and the pre-auth form,
      // so a card-present plan payment always stores the tapped card.
      saveCard: saveCard || !!recurringPayment,
      ...(recurringPayment && { recurringPayment }),
      ...(planId && { planId }),
      ...(scheduleId && { scheduleId }),
      ...(updatePlanId && { updatePlanId })
    },
    appliedBalance
  });

  if (kioskCommandRes?.code !== 0) {
    throw new Error(kioskCommandRes);
  }

  showAlert({
    icon: 'outbound-fax-success',
    color: 'success',
    title: `Payment request sent`,
    message: (
      <Typography variant="p" size="small" className="block">
        Payment request sent to kiosk{' '}
        <span className="font-semibold">{kioskCommandRes?.kioskName}</span> for{' '}
        <span className="font-semibold">{mString(chargeAmount + tip)}</span>
        {tip > 0 && ` (including a ${mString(tip)} tip)`}
      </Typography>
    ),
    duration: 15000,
    variant: 'light'
  });

  return {
    code: 0,
    kioskName: kioskCommandRes?.kioskName,
    kioskId: methodDetail,
    awaitingKiosk: true,
    externalInvoiceToken: extInvoice?.token ?? null,
    patientId: extInvoice?.patient_id ?? patientId ?? null
  };
};

const planExternalInvoiceHandler = async (
  navigate,
  { patientId, amount, planId, scheduleId, surchargeAmount, surchargeEnabled }
) => {
  const amountCents = Math.round(Number(amount || 0));
  if (!amountCents || amountCents <= 0) {
    showAlert({ color: 'danger', title: 'Enter an amount greater than zero' });
    return { code: 1 };
  }

  const effectiveSurcharge = surchargeEnabled ? Math.round(Number(surchargeAmount || 0)) : 0;

  const memo = scheduleId
    ? 'Payment plan installment'
    : planId
      ? 'Payment plan payoff'
      : 'Payment request';

  const payload = {
    invoices: [
      {
        amount_cents: amountCents,
        total_amount_cents: amountCents + effectiveSurcharge,
        memo,
        internal_invoice_ids: [],
        patient: { id: patientId },
        tax_percentage: 0,
        surcharge_amount: effectiveSurcharge,
        aeob: false,
        is_detailed: false,
        type: 'kiosk',
        internal_details: [],
        ...(planId && { payment_plan_id: planId }),
        ...(scheduleId && { payment_plan_schedule_id: scheduleId })
      }
    ],
    skipConfirmation: true
  };

  const result = await createExternalInvoices(navigate, payload);
  const externalInvoice = result?.invoices?.[0] || null;

  if (result?.code !== 0 || !externalInvoice) {
    showAlert({ color: 'danger', title: result?.error || "Couldn't create the invoice" });
    return { code: result?.code ?? 1 };
  }

  return {
    code: 0,
    externalInvoice,
    patient_id: patientId
  };
};

const requestPaymentHandler = async (
  navigate,
  {
    patientId,
    updatePlanId,
    recurringPayment,
    amount: grossAmount,
    gratuity,
    appointmentId,
    invoices,
    surchargeAmount,
    planId,
    scheduleId,
    surchargeEnabled,
    preAuthOption,
    collectionMode,
    includedDocuments,
    patientFormIds,
    pendingPatient
  }
) => {
  const isPendingPatient = !!pendingPatient && !patientId;
  // The patient pays the link later and no tip is taken there.
  const amount = amountWithoutGratuity({ amount: grossAmount, gratuity });
  const isPlanPayment = (!!scheduleId || !!planId) && !recurringPayment && !updatePlanId;

  if (isPlanPayment) {
    return planExternalInvoiceHandler(navigate, {
      patientId,
      amount,
      planId,
      scheduleId,
      surchargeAmount,
      surchargeEnabled
    });
  }

  const isPreAuthRequest =
    !!recurringPayment || !!updatePlanId || !!preAuthOption || collectionMode === 'pre-authorize';

  const res = await createInstantPacket(navigate, {
    ...documentPacketForms(includedDocuments, isPreAuthRequest ? 'pre-auth' : 'payment', {
      patientFormIds
    }),
    patientId,
    ...(isPendingPatient ? { pendingPatient: true } : { appointmentId }),
    ...(updatePlanId && { updatePlanId }),
    ...(recurringPayment && {
      recurringPayment,
      amount,
      invoices,
      surchargeAmount
    }),
    ...(isPreAuthRequest && preAuthOption && { preAuthOption }),
    ...(!isPreAuthRequest && {
      payment: {
        amount: Math.round(Number(amount) || 0),
        surchargeAmount: Math.round(Number(surchargeAmount) || 0),
        invoiceIds: iaRa(invoices)
          .map((invoice) => invoice?.id)
          .filter(Boolean)
      }
    })
  });

  if (!res?.token) {
    const title = isPreAuthRequest
      ? "Couldn't create the pre-authorization link"
      : "Couldn't create the payment link";
    showAlert({ color: 'danger', title, ...(res?.error && { message: res.error }) });
    return { code: res?.code || 1, error: res?.error || title };
  }

  return {
    code: 0,
    instantPacketToken: res.token,
    patient_id: patientId,
    ...(res.planId && { planId: res.planId })
  };
};

const getUpfront = (props) => Number(props?.recurringPayment?.upfrontPayment) || 0;

export const paymentMethodApiElavon = {
  card: (navigate, props) =>
    sendCommandToKiosk(navigate, { type: 'card', data: { processor: 'elavon' }, ...props }),
  'saved-cards': elavonSavedCardHandler,
  'credit-card': chargeElavonOnlineHandler,
  'pre-auth': (navigate, props) =>
    chargeElavonOnlineHandler(navigate, { tokenOnly: true, ...props }),
  'pre-auth-deferred': elavonPreAuthDeferredHandler,
  'recurring-charge': (navigate, props) => {
    const upfront = getUpfront(props);
    if (upfront > 0) {
      return chargeElavonOnlineHandler(navigate, { ...props, amount: upfront });
    }
    return chargeElavonOnlineHandler(navigate, { tokenOnly: true, ...props });
  },
  'request-payment': requestPaymentHandler
};

export const paymentMethodApiPayroc = {
  card: (navigate, props) =>
    sendCommandToKiosk(navigate, { type: 'card', data: { processor: 'payroc' }, ...props }),
  'saved-cards': (navigate, props) =>
    chargePayrocPaymentHandler(navigate, { type: 'card', ...props, saveCard: false }),
  ach: (navigate, props) => chargePayrocPaymentHandler(navigate, { type: 'ach', ...props }),
  'credit-card': (navigate, props) =>
    chargePayrocPaymentHandler(navigate, { type: 'card', ...props }),
  'pre-auth': (navigate, props) => saveCardHandler(navigate, { type: 'card', ...props }),
  'pre-auth-deferred': (_navigate, props) =>
    Promise.resolve({
      code: 0,
      token: props?.token,
      type: props?.type || 'card',
      terminalId: props?.terminalId,
      preAuthForm: props?.preAuthForm,
      deferred: true
    }),
  'recurring-charge': (navigate, props) => {
    const upfront = getUpfront(props);
    if (upfront > 0) {
      return chargePayrocPaymentHandler(navigate, { type: 'card', ...props, amount: upfront });
    }
    return saveCardHandler(navigate, { type: 'card', ...props });
  },
  vpayment: (navigate, props) => {
    return chargePayrocPaymentHandler(navigate, { type: 'card', ...props });
  }
};

export const genericMethodsApi = {
  cash: chargeCashHandler,
  check: chargeCheckHandler,
  'outside-financing': chargeOutsideFinancingHandler,
  'defer-payment': noPaymentSelectedHandler,
  'request-payment': requestPaymentHandler,
  'applied-balance': appliedBalanceHandler
};
